/**
 * Customers — the tenant's directory of everyone who books, registers or joins
 * a queue. One record per person; the columns are the tenant's own, saved for
 * everyone in the organisation and edited through the Columns panel.
 *
 * Everything that narrows the list happens ON THE SERVER: `q`, `customerTypeId`
 * / `untyped`, `sort` + `direction`, `page` + `limit`. Nothing is filtered or
 * sorted across the loaded page — a page is a window on the directory, not the
 * directory. `pagination.total` is the filtered count and the page boundary;
 * `summary.*` are the filter-INDEPENDENT directory totals behind the KPI cards,
 * which is why the two numbers legitimately differ while a filter is on.
 *
 * Read-only. There is no create/edit form, export, message, book or register
 * action on this surface; the one supported write is the customer-type
 * assignment on the detail, and the shared column layout through Columns.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Btn, Dropdown, Icon, Input, PageState, Pagination, SegmentedControl, T, Table, alpha, type TableColumn } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { DataSourceStrip } from "../../shell/opSource";
import { useNav } from "../../shell/router";
import { can } from "../../shell/access";
import { useIsMobile } from "../../shell/useResponsive";
import { useT } from "../../lib/i18n";
import { useCustomerTypeCatalog } from "./customer-types";
import { TypeCatalogNotice } from "./CustomerTypeCard";
import {
  CUSTOMER_OPS, DIRECTORY_LIMIT_DEFAULT, getCustomersSettings, listBookingCustomers, useScopedRead,
  type CustomerDirectoryRecord, type CustomerListSortKey, type DirectoryPage,
} from "./customers-data";
import {
  CUSTOMER_LIST_SORT_KEYS, EM_DASH, PLATFORM_DEFAULT_COLUMNS, PLATFORM_DEFAULT_SORT, isSortableColumn, normalizeColumns,
} from "./customer-columns";
import { CustomerCell, Kpi, ReadError, useColumnLabel, useCustomerNoun, useIcu, initialsOf } from "./customers-shared";
import ColumnsPanel from "./ColumnsPanel";

const UNTYPED = "__untyped";
const ALL = "__all";
const SEARCH_DEBOUNCE_MS = 300;

export default function CustomersList() {
  const t = useT();
  const icu = useIcu();
  const nav = useNav();
  const { noun, nounPlural } = useCustomerNoun();
  const phone = useIsMobile(640);
  const columnLabel = useColumnLabel();
  const catalog = useCustomerTypeCatalog(can("customer_types.read"));

  /* ── tenant list configuration ─────────────────────────────────────── */
  /* The saved layout is a REFINEMENT of the directory, never its precondition
     (CD docket 2026-09-15 item 3b). While this read is in flight, or if it
     fails outright, the screen renders the platform default columns and the
     platform default sort and shows records — a degraded column
     configuration, not an empty screen. */
  const settingsRead = useScopedRead("customers-settings", getCustomersSettings);
  const settings = settingsRead.data?.data ?? null;
  const columns = useMemo(
    () => normalizeColumns(settings ? settings.columns : PLATFORM_DEFAULT_COLUMNS),
    [settings],
  );

  /* ── query state ───────────────────────────────────────────────────── */
  const [typed, setTyped] = useState("");
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>(ALL);
  const [page, setPage] = useState(1);
  const [sortOverride, setSortOverride] = useState<{ key: CustomerListSortKey; direction: "asc" | "desc" } | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const columnsButtonRef = useRef<HTMLButtonElement | null>(null);

  // Debounced, server-side search. Every change resets to page 1 — a filtered
  // result set has different page boundaries, so keeping the page number would
  // land the operator on an empty page of a non-empty result.
  useEffect(() => {
    const id = window.setTimeout(() => { setQ(typed.trim()); setPage(1); }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [typed]);

  const sort = sortOverride ?? settings?.defaultSort ?? PLATFORM_DEFAULT_SORT;
  const externalIdSystem = settings?.externalIdSystem ?? null;

  /* Event-scoped directory (2026-09-14, operator). `/customers?eventId=<id>`
     narrows the list to the people registered for one event — the destination
     the Events drawer's "open the full list" now uses, replacing the retired
     Guests screen. The id lives in the URL, so the filtered list is
     shareable and survives reload; clearing it is a navigation, not local
     state, for the same reason. */
  const eventId = typeof nav.params.eventId === "string" ? nav.params.eventId : "";

  const queryKey = [
    "customers", q, typeFilter, page, sort.key, sort.direction, externalIdSystem ?? "-",
    eventId || "-",
  ].join("|");

  /* Ungated on the settings read. The key carries the effective sort and
     external-id system, so if the saved layout differs from the defaults this
     re-reads exactly once when it lands, and not at all when it matches. */
  const directory = useScopedRead<DirectoryPage>(
    queryKey,
    () => listBookingCustomers({
      q: q || undefined,
      eventId: eventId || undefined,
      customerTypeId: typeFilter !== ALL && typeFilter !== UNTYPED ? typeFilter : undefined,
      untyped: typeFilter === UNTYPED,
      externalIdSystem,
      sort: sort.key,
      direction: sort.direction,
      page,
      limit: DIRECTORY_LIMIT_DEFAULT,
    }),
  );

  const rows = directory.data?.data ?? [];
  const pagination = directory.data?.pagination ?? null;
  const summary = directory.data?.summary ?? null;
  const total = pagination?.total ?? 0;
  const totalPages = pagination && pagination.limit > 0 ? Math.max(1, Math.ceil(pagination.total / pagination.limit)) : 1;
  const filtersActive = !!q || typeFilter !== ALL || !!eventId;

  /* The responder echoes the event it narrowed to. Absent while the read is in
     flight, and absent for good if the backend ignored `eventId` — the banner
     says which, rather than letting an unfiltered directory pass for a roster. */
  const eventFilter = directory.data?.summary?.event ?? null;
  const eventFilterHonoured = !!directory.data && !!eventFilter;
  const clearEventFilter = useCallback(() => nav.go("customers"), [nav]);

  const openCustomer = useCallback((id: string) => nav.go("customers.detail", { customerId: id }), [nav]);

  const changeType = (next: string) => { setTypeFilter(next); setPage(1); };
  const changeSort = (key: CustomerListSortKey) => {
    setSortOverride((prev) => {
      const base = prev ?? sort;
      return base.key === key
        ? { key, direction: base.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" };
    });
    setPage(1);
  };

  /* ── type filter chips ─────────────────────────────────────────────── */
  const typeOptions = useMemo(() => {
    const opts: Array<{ value: string; label: string; count?: number }> = [
      { value: ALL, label: t("customers.filters.all", "All"), count: summary?.customers },
    ];
    for (const ct of summary?.customerTypes ?? []) opts.push({ value: ct.id, label: ct.name, count: ct.count });
    // "Untyped" is offered only when it would match something. A selected
    // Untyped filter keeps its chip so the active filter is never invisible.
    if ((summary?.untyped ?? 0) > 0 || typeFilter === UNTYPED) {
      opts.push({ value: UNTYPED, label: t("customers.filters.untyped", "Untyped"), count: summary?.untyped });
    }
    return opts;
  }, [summary, typeFilter, t]);

  // A type that vanished from the directory summary (archived, rescoped) must
  // not leave the list filtered by an invisible reason.
  useEffect(() => {
    if (typeFilter === ALL || typeFilter === UNTYPED || !summary) return;
    if (!summary.customerTypes.some((ct) => ct.id === typeFilter)) { setTypeFilter(ALL); setPage(1); }
  }, [summary, typeFilter]);

  /* ── table columns ─────────────────────────────────────────────────── */
  const externalIdLabel = externalIdSystem ? t(`customers.externalIdSystem.${externalIdSystem}`, externalIdSystem.toUpperCase()) : null;

  const tableColumns: Array<TableColumn<CustomerDirectoryRecord>> = columns.map((id) => {
    const label = columnLabel(id);
    const sortable = isSortableColumn(id);
    const align: "left" | "right" = ["appointments", "eventRegistrations", "eventCheckIns", "queueJoins"].includes(id) ? "right" : "left";
    const active = sort.key === id;
    return {
      id,
      align,
      minWidth: id === "name" ? 220 : undefined,
      header: sortable ? (
        <button
          type="button"
          onClick={() => changeSort(id as CustomerListSortKey)}
          aria-label={icu("customers.list.sortAriaLabel", "Sort by {column}", { column: label })}
          style={{
            display: "inline-flex", alignItems: "center", gap: 5, border: 0, background: "transparent",
            padding: 0, font: "inherit", color: "inherit", cursor: "pointer", letterSpacing: "inherit", textTransform: "inherit",
          }}
        >
          <span>{label}</span>
          <span aria-hidden="true" style={{ color: active ? T.n1 : T.n4, fontSize: 10 }}>
            {active ? (sort.direction === "asc" ? "\u25B2" : "\u25BC") : "\u21C5"}
          </span>
        </button>
      ) : label,
      cell: (row) => <CustomerCell row={row} columnId={id} catalog={catalog} externalIdLabel={externalIdLabel} />,
    };
  });

  const sortAnnouncement = (() => {
    const label = columnLabel(sort.key);
    return sort.direction === "asc"
      ? icu("customers.list.sortedAscending", "Sorted by {column}, ascending", { column: label })
      : icu("customers.list.sortedDescending", "Sorted by {column}, descending", { column: label });
  })();

  /* ── settings failure is not an empty directory ────────────────────── */
  const settingsFailure = settingsRead.failure;

  return (
    <Screen rail="customers">
      {/* On a phone the page is the third tab beside My Day and My Calendar: the
          title alone, no subtitle, no back link — the bottom bar is the navigation. */}
      <PageHeader
        title={nounPlural}
        breadcrumb={false}
        subtitle={phone ? undefined : icu(
          "customers.list.subtitle",
          "Everyone who books, registers or joins a queue \u2014 one record per {noun}, with their full history.",
          { noun: noun.toLowerCase() },
        )}
      />
      <DataSourceStrip screen="customers" ops={CUSTOMER_OPS} reqId="REQ-069" />

      <div style={{ padding: phone ? "10px 12px 40px" : "14px 24px 48px", display: "flex", flexDirection: "column", gap: phone ? 12 : 16, minWidth: 0 }}>
        {eventId ? (
          <div role="status" style={{
            display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap",
            padding: "10px 14px", borderRadius: 8, fontSize: 12.5, color: T.n1,
            /* An ACTIVE FILTER has to look active. A pale tint alone read as
               page furniture against this canvas, which is itself pale — the
               operator could not see that the list was narrowed. Border and a
               filled icon tile carry it now; the tint alone never did. */
            background: T.white,
            border: `1px solid ${eventFilterHonoured ? alpha(T.p1, "55") : alpha(T.orange1, "66")}`,
            boxShadow: `inset 3px 0 0 ${eventFilterHonoured ? T.p1 : T.orange1}`,
          }}>
            <span style={{
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              width: 22, height: 22, borderRadius: 6, flexShrink: 0,
              background: eventFilterHonoured ? T.p1 : T.orange1, color: T.onColor,
            }}>
              {eventFilterHonoured ? <Icon.calendar size={13} /> : <Icon.warn size={13} />}
            </span>
            <span style={{ flex: 1, minWidth: 220 }}>
              {directory.loading
                ? icu("customers.list.eventFilter.loading", "Narrowing to the {nounPlural} registered for this event\u2026", { nounPlural: nounPlural.toLowerCase() })
                : eventFilterHonoured
                  ? icu("customers.list.eventFilter.on", "Showing only the {nounPlural} registered for {event}.", { nounPlural: nounPlural.toLowerCase(), event: eventFilter.title })
                  : t("customers.list.eventFilter.unsupported", "This directory can\u2019t filter by event yet, so every record is shown \u2014 not just this event\u2019s registrations.")}
            </span>
            <Btn variant="secondary" size="sm" onClick={clearEventFilter}>
              {icu("customers.list.eventFilter.clear", "All {nounPlural}", { nounPlural })}
            </Btn>
          </div>
        ) : null}

        {/* Filter-independent directory totals for the authorised scope. On a
            phone they collapse to the same dark strip My Day carries. */}
        {phone ? (
          <div style={{ borderRadius: 10, background: "#14102E", color: T.onColor, padding: "12px 14px", display: "flex", flexWrap: "wrap", gap: "6px 18px", alignItems: "baseline" }}>
            <span style={{ fontSize: 22, fontWeight: 700, lineHeight: 1 }}>{summary ? summary.customers : EM_DASH} <span style={{ fontSize: 12.5, fontWeight: 500, opacity: 0.75 }}>{nounPlural.toLowerCase()}</span></span>
            <span style={{ fontSize: 12.5, opacity: 0.85 }}>{summary ? summary.appointments : EM_DASH} {t("customers.kpi.appointments", "Appointments").toLowerCase()} · {summary ? summary.eventCheckIns : EM_DASH} {t("customers.kpi.eventCheckIns", "Event check-ins").toLowerCase()} · {summary ? summary.queueJoins : EM_DASH} {t("customers.kpi.queueJoins", "Queue joins").toLowerCase()}</span>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
            <Kpi label={nounPlural} value={summary ? summary.customers : EM_DASH} />
            <Kpi label={t("customers.kpi.appointments", "Appointments")} value={summary ? summary.appointments : EM_DASH} />
            <Kpi label={t("customers.kpi.eventCheckIns", "Event check-ins")} value={summary ? summary.eventCheckIns : EM_DASH} />
            <Kpi label={t("customers.kpi.queueJoins", "Queue joins")} value={summary ? summary.queueJoins : EM_DASH} />
          </div>
        )}

        <div style={{ display: "flex", gap: phone ? 8 : 12, alignItems: "center", flexWrap: "wrap" }}>
          {/* Phone: search first and full width (44px tall to tap), then the type
              filter as a scrolling row; desktop keeps the one toolbar line. */}
          {phone ? (
            /* Pills that wrap, never a strip that runs off the screen. Same pill
               grammar as the My Day forms: purple when chosen, outlined otherwise. */
            <div role="group" aria-label={icu("customers.filters.ariaLabel", "Filter {nounPlural} by type", { nounPlural })}
              style={{ flex: "1 1 100%", display: "flex", flexWrap: "wrap", gap: 8, order: 0 }}>
              {typeOptions.map((o) => {
                const on = o.value === typeFilter;
                return (
                  <button key={o.value} type="button" onClick={() => changeType(o.value)} aria-pressed={on}
                    style={{ height: 36, padding: "0 14px", borderRadius: 999, cursor: "pointer", fontFamily: T.font, fontSize: 13, fontWeight: 600, whiteSpace: "nowrap",
                      background: on ? T.p1 : T.white, color: on ? T.onColor : T.n1, border: `1px solid ${on ? T.p1 : T.border}` }}>
                    {o.label}{o.count != null ? <span style={{ marginLeft: 6, fontWeight: 500, opacity: on ? 0.85 : 0.6 }}>{o.count}</span> : null}
                  </button>
                );
              })}
            </div>
          ) : (
            <div style={{ flex: "0 0 auto", minWidth: 0, order: 1 }}>
              <SegmentedControl<string>
                size="sm"
                ariaLabel={icu("customers.filters.ariaLabel", "Filter {nounPlural} by type", { nounPlural })}
                value={typeFilter}
                onChange={changeType}
                options={typeOptions}
              />
            </div>
          )}
          <div style={{ flex: phone ? "1 1 100%" : 1, minWidth: phone ? 0 : 220, order: phone ? -1 : 2 }}>
            <Input
              size={phone ? "md" : "sm"}
              type="search"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              iconLeft={<Icon.search size={13} />}
              placeholder={icu("customers.list.searchPlaceholder", "Search {nounPlural} by name, email or ID", { nounPlural: nounPlural.toLowerCase() })}
              aria-label={icu("customers.list.searchAriaLabel", "Search {nounPlural}", { nounPlural: nounPlural.toLowerCase() })}
            />
          </div>
          {phone ? (
            <div style={{ flex: "1 1 100%", order: 3 }}>
              <Dropdown<string>
                size="md"
                portal
                aria-label={t("customers.list.sortControl", "Sort")}
                value={sort.key}
                onChange={(k) => changeSort(k as CustomerListSortKey)}
                options={CUSTOMER_LIST_SORT_KEYS.filter((k) => columns.includes(k)).map((k) => ({ value: k, label: columnLabel(k) }))}
              />
            </div>
          ) : null}
          {can("settings.write") && !phone ? (
            <Btn
              ref={columnsButtonRef}
              variant="secondary"
              size="sm"
              icon={<Icon.grid size={13} />}
              onClick={() => setPanelOpen(true)}
            >{t("customers.columnsPanel.open", "Columns")}</Btn>
          ) : null}
        </div>

        <TypeCatalogNotice catalog={catalog} />

        {settingsFailure ? (
          <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 8 }}>
            <ReadError
              message={settingsFailure.denied
                ? t("customers.list.settingsDenied", "Your role can\u2019t read this organisation\u2019s list configuration yet, so the default columns are shown.")
                : `${t("customers.list.settingsFailed", "The column configuration couldn\u2019t be read, so the default columns are shown.")} ${settingsFailure.message}`}
              onRetry={settingsFailure.denied ? undefined : settingsRead.reload}
              retryLabel={t("common.actions.retry", "Retry")}
            />
          </div>
        ) : null}

        <PageState
            noun={nounPlural.toLowerCase()}
            loading={directory.loading}
            error={directory.failure ? directory.failure.message : null}
            empty={!directory.loading && !directory.failure && rows.length === 0}
            filtered={filtersActive}
            skeleton="table"
            skeletonColumns={Math.min(columns.length || 4, 6)}
            onRetry={directory.reload}
            onClearFilters={filtersActive ? () => { setTyped(""); setQ(""); setTypeFilter(ALL); setPage(1); if (eventId) nav.go("customers"); } : undefined}
            errorTitle={t("customers.list.readFailed.title", "This list couldn\u2019t be loaded")}
            errorBody={t("customers.list.readFailed.body", "The directory read failed. Nothing has been lost \u2014 try again.")}
            retryLabel={t("common.actions.retry", "Retry")}
            emptyTitle={t("customers.list.empty.title", "Nothing here yet")}
            emptyBody={t("customers.list.empty.body", "Records appear here as soon as someone books, registers or joins a queue.")}
            filteredTitle={t("customers.list.emptyFiltered.title", "No matches")}
            filteredBody={t("customers.list.emptyFiltered.body", "Clear the search or choose a different type.")}
            clearFiltersLabel={t("common.actions.clearFilters", "Clear filters")}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <p aria-live="polite" style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap", margin: 0 }}>
                {sortAnnouncement}
              </p>

              {phone ? (
                <MobileRows rows={rows} columns={columns} catalog={catalog} externalIdLabel={externalIdLabel} columnLabel={columnLabel} onOpen={openCustomer} />
              ) : (
                <Table<CustomerDirectoryRecord>
                  testid="customers-table"
                  caption={icu("customers.list.tableAriaLabel", "{nounPlural}", { nounPlural })}
                  columns={tableColumns}
                  rows={rows}
                  getRowId={(r) => r.id}
                  sort={{ columnId: sort.key, direction: sort.direction }}
                  onRowClick={(r) => openCustomer(r.id)}
                  emptyState={t("customers.list.emptyFiltered.title", "No matches")}
                />
              )}

              <div style={{ display: "flex", gap: 12, alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
                <span style={{ fontSize: 12.5, color: T.soft }}>
                  {icu("customers.list.showingCount", "Showing {shown} of {total}", { shown: rows.length, total })}
                </span>
                {totalPages > 1 ? (
                  <Pagination
                    size="sm"
                    showStatus={false}
                    aria-label={icu("customers.list.paginationAriaLabel", "{nounPlural} pages", { nounPlural })}
                    totalPages={totalPages}
                    currentPage={pagination?.page ?? page}
                    onPageChange={setPage}
                  />
                ) : null}
              </div>
            </div>
          </PageState>
      </div>

      {panelOpen && settings ? (
        <ColumnsPanel
          settings={settings}
          onClose={() => { setPanelOpen(false); columnsButtonRef.current?.focus(); }}
          onSaved={() => { settingsRead.reload(); }}
        />
      ) : null}
    </Screen>
  );
}

/**
 * Below 640px the table becomes one card per record: Name first, then the
 * configured fields BENEATH it in the saved order — the same layout decision,
 * re-flowed. No horizontal page overflow at 320px.
 */
function MobileRows({ rows, columns, catalog, externalIdLabel, columnLabel, onOpen }: {
  rows: CustomerDirectoryRecord[];
  columns: string[];
  catalog: ReturnType<typeof useCustomerTypeCatalog>;
  externalIdLabel: string | null;
  columnLabel: (id: string) => string;
  onOpen: (id: string) => void;
}) {
  return (
    <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 8 }}>
      {rows.map((row) => {
        const shown = columns.filter((id) => id !== "name" && id !== "externalId").slice(0, 3);
        return (
        <li key={row.id}>
          <button
            type="button"
            onClick={() => onOpen(row.id)}
            style={{
              width: "100%", textAlign: "left", background: T.white, border: `1px solid ${T.border}`,
              borderRadius: 10, padding: "12px 12px 12px 14px", cursor: "pointer", fontFamily: T.font,
              display: "flex", alignItems: "center", gap: 12, minWidth: 0, minHeight: 64,
            }}
          >
            <span style={{
              width: 36, height: 36, borderRadius: 999, background: T.p4, color: T.p1,
              display: "grid", placeItems: "center", fontSize: 12, fontWeight: 700, flexShrink: 0,
            }}>{initialsOf(row.displayName)}</span>
            <span style={{ flex: 1, display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
              <span style={{ display: "flex", alignItems: "baseline", gap: 8, minWidth: 0 }}>
                <span style={{ fontSize: 15, fontWeight: 700, color: T.n1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.displayName}</span>
                {row.externalId?.value ? (
                  <span style={{ fontFamily: T.fontMono, fontSize: 11, color: T.soft, flexShrink: 0 }}>{row.externalId.value}</span>
                ) : null}
              </span>
              <span style={{ display: "flex", flexWrap: "wrap", gap: "2px 10px", fontSize: 12.5, color: T.soft, minWidth: 0 }}>
                {shown.map((id) => (
                  <span key={id} style={{ display: "inline-flex", gap: 4, alignItems: "baseline", minWidth: 0 }}>
                    <span style={{ color: T.n1 }}><CustomerCell row={row} columnId={id} catalog={catalog} externalIdLabel={externalIdLabel} /></span>
                    <span>{columnLabel(id).toLowerCase()}</span>
                  </span>
                ))}
              </span>
            </span>
            <span style={{ color: T.n2, display: "inline-flex", flexShrink: 0 }}><Icon.chev dir="right" size={16} /></span>
          </button>
        </li>
        );
      })}
    </ul>
  );
}
